import { Location } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useNudge } from '@cobliteam/hooks/src/useNudge';
import { hideChecklistComplianceNudge, isChecklistComplianceNudgeHidden } from '@cobliteam/hooks/src/useNudge/checklistComplianceNudgeVisibility';
import type { Nudge, NudgeSurface } from '@cobliteam/hooks/src/useNudge/types';
import { useTranslation } from '@cobliteam/i18n';
import { permissions, usePermissions } from '@cobliteam/permissions';
import NudgeMessage from '@cobliteam/ui-business/components/Nudge/NudgeMessage';
import { useInitializationStatus } from '@launchdarkly/react-sdk';
import { useHistory, useLocation } from 'react-router-dom';

export const CHECKLIST_COMPLIANCE_CREATE_NUDGE_ID = 'checklist-compliance-create';
export const CHECKLIST_COMPLIANCE_EDIT_NUDGE_ID = 'checklist-compliance-edit';

/**
 * Suggestion for the checklist compliance launch (wave 1). The `checklist-compliance-floating-card`
 * flag only picks the copy — `true` for fleets with no checklist (a segment synced from Snowflake),
 * `false` for the rest — and never decides whether the card shows.
 *
 * Unlike the other nudges, hiding is persisted per fleet: the launch plan says this card is dismissed
 * once and never comes back. Creating a checklist hides it too, from `createChecklist`.
 */
export const useChecklistComplianceNudge = (surface: NudgeSurface = 'floating-card'): Nudge | null => {
  const { t } = useTranslation('suggestions');
  const history = useHistory();
  const { pathname } = useLocation();
  const { isAccessAllowed } = usePermissions();
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id ?? '';
  const { checklistComplianceFloatingCard: isFleetWithoutChecklists } = useFeatureFlags();
  // `false` is also a variant, so the catalog fallback would show "edit" until LaunchDarkly answers.
  const areFlagsReady = useInitializationStatus().status === 'complete';

  const id = isFleetWithoutChecklists ? CHECKLIST_COMPLIANCE_CREATE_NUDGE_ID : CHECKLIST_COMPLIANCE_EDIT_NUDGE_ID;
  const variant = isFleetWithoutChecklists ? 'create' : 'edit';

  const canManageChecklists = isAccessAllowed(permissions.FULL_CONTROL_CHECKLIST);
  const isOnChecklistPages = pathname.startsWith(Location.CHECKLIST);

  const { shouldShow, dismiss, accept } = useNudge({
    id,
    kind: 'suggestion',
    surface,
    scope: fleetId,
    useShouldShow: () => areFlagsReady && !!fleetId && canManageChecklists && !isOnChecklistPages && !isChecklistComplianceNudgeHidden(fleetId),
  });

  if (!shouldShow) return null;

  const dismissForGood = (reason?: string) => {
    hideChecklistComplianceNudge(fleetId);
    dismiss(reason);
  };

  const title = t(`checklist_compliance.${variant}.title`);

  return {
    id,
    kind: 'suggestion',
    priority: 2,
    tagLabel: t('checklist_compliance.tag_label'),
    title,
    dismiss: dismissForGood,
    render: () => (
      <NudgeMessage
        title={title}
        description={t(`checklist_compliance.${variant}.description`)}
        ctaLabel={t(`checklist_compliance.${variant}.cta`)}
        onCta={() => {
          accept({ variant });
          if (isFleetWithoutChecklists) {
            // Stays until the checklist is saved — opening the drawer and giving up is not a conversion.
            history.push(`${Location.CHECKLIST_ALL}?create=true`);
            return;
          }
          hideChecklistComplianceNudge(fleetId);
          history.push(Location.CHECKLIST_ALL);
        }}
        dismissLabel={t('checklist_compliance.dismiss')}
        onDismiss={() => dismissForGood('dismiss-button')}
      />
    ),
  };
};

export default useChecklistComplianceNudge;
